/**
 * Maps api/v2/artist/details/ `incompleted_sections` to nine profile-completion segments.
 * Past Performances is not part of this model (non-mandatory).
 */

export const PROFILE_COMPLETION_SEGMENT_COUNT = 9;

export type ProfileCompletionSegmentId =
  | 'gallery'
  | 'personal'
  | 'performance'
  | 'usp'
  | 'story'
  | 'achievements'
  | 'social'
  | 'event_pricing'
  | 'profile_photo';

type SegmentDef = {
  id: ProfileCompletionSegmentId;
  label: string;
  navPath: string;
  /** True if this segment still needs work (any listed key present in incompleted_sections). */
  matchIncomplete: (incomplete: Set<string>) => boolean;
};

const SEGMENTS: SegmentDef[] = [
  {
    id: 'gallery',
    label: 'Gallery',
    navPath: '/profile/gallery',
    matchIncomplete: (s) => s.has('gallery_images') || s.has('gallery_videos'),
  },
  {
    id: 'personal',
    label: 'Personal Details',
    navPath: '/profile/personal-details',
    matchIncomplete: (s) => s.has('gender') || s.has('location'),
  },
  {
    id: 'performance',
    label: 'Performance Details',
    navPath: '/profile/performance-details',
    matchIncomplete: (s) => s.has('performing_languages') || s.has('performance_duration'),
  },
  {
    id: 'usp',
    label: 'Unique Sell Point',
    navPath: '/profile/usp',
    matchIncomplete: (s) => s.has('usp'),
  },
  {
    id: 'story',
    label: 'Story',
    navPath: '/profile/story',
    matchIncomplete: (s) => s.has('story'),
  },
  {
    id: 'achievements',
    label: 'Achievements',
    navPath: '/profile/awards-achievements',
    matchIncomplete: (s) => s.has('achievements') || s.has('awards'),
  },
  {
    id: 'social',
    label: 'Social Media',
    navPath: '/profile/social-media',
    /** Backend may send `social_media` and/or per-network keys on `incompleted_sections`. */
    matchIncomplete: (s) =>
      s.has('social_media') || s.has('connect_instagram') || s.has('connect_youtube'),
  },
  {
    id: 'event_pricing',
    label: 'Event Pricing & Billing',
    navPath: '/profile/event-essentials',
    matchIncomplete: (s) =>
      s.has('email_verify') || s.has('phone_verify') || s.has('event_pricing'),
  },
  {
    id: 'profile_photo',
    label: 'Profile photo',
    navPath: '/profile',
    matchIncomplete: (s) => s.has('profile_pic_url'),
  },
];

/** Success Checklist route `id` → segment index in SEGMENTS, or null (e.g. past performances). */
export const PROFILE_CHECKLIST_ROUTE_TO_SEGMENT_INDEX: Record<string, number | null> = {
  gallery: 0,
  'personal-details': 1,
  'performance-details': 2,
  usp: 3,
  story: 4,
  'awards-achievements': 5,
  'past-performances': null,
  'social-media': 6,
};

export const PROFILE_EVENT_PRICING_SEGMENT_INDEX = 7;
export const PROFILE_PHOTO_SEGMENT_INDEX = 8;

export type ProfileCompletionDerived = {
  incompleteSet: Set<string>;
  segmentComplete: boolean[];
  completedCount: number;
  incompleteLabels: string[];
  /** Artist details have been loaded at least once. */
  isReady: boolean;
  /** `incompleted_sections` missing on payload — show neutral bar, not a false 9/9. */
  isIndeterminate: boolean;
};

export function deriveProfileCompletion(
  artistDetails: { incompleted_sections?: string[] } | null
): ProfileCompletionDerived {
  if (artistDetails == null) {
    return {
      incompleteSet: new Set(),
      segmentComplete: Array(PROFILE_COMPLETION_SEGMENT_COUNT).fill(false) as boolean[],
      completedCount: 0,
      incompleteLabels: [],
      isReady: false,
      isIndeterminate: true,
    };
  }

  const raw = artistDetails.incompleted_sections;
  const isIndeterminate = raw === undefined;
  const incompleteSet = new Set(Array.isArray(raw) ? raw : []);
  const segmentComplete = SEGMENTS.map((seg) => !seg.matchIncomplete(incompleteSet));
  const completedCount = segmentComplete.filter(Boolean).length;
  const incompleteLabels = SEGMENTS.filter((_seg, i) => !segmentComplete[i]).map((s) => s.label);

  return {
    incompleteSet,
    segmentComplete,
    completedCount,
    incompleteLabels,
    isReady: true,
    isIndeterminate,
  };
}

export function checklistBlockNeedsAttention(
  blockId: string,
  segmentComplete: boolean[]
): boolean {
  const idx = PROFILE_CHECKLIST_ROUTE_TO_SEGMENT_INDEX[blockId];
  if (idx === null || idx === undefined) return false;
  return !segmentComplete[idx];
}

export function segmentNeedsAttention(segmentIndex: number, segmentComplete: boolean[]): boolean {
  return segmentIndex >= 0 && segmentIndex < segmentComplete.length && !segmentComplete[segmentIndex];
}

/** Hide profile-blocks UI when all segments are done (known payload). */
export function isProfileCompletionFullyComplete(state: ProfileCompletionDerived): boolean {
  return (
    state.isReady &&
    !state.isIndeterminate &&
    state.completedCount >= PROFILE_COMPLETION_SEGMENT_COUNT
  );
}

export type IncompleteNavItem = {
  segmentId: ProfileCompletionSegmentId;
  label: string;
  path: string;
};

/** Incomplete segments with routes for checklist navigation. */
export function getIncompleteNavItems(state: ProfileCompletionDerived): IncompleteNavItem[] {
  if (!state.isReady || state.isIndeterminate) return [];
  return SEGMENTS.map((seg, i) => ({ seg, complete: state.segmentComplete[i] }))
    .filter(({ complete }) => !complete)
    .map(({ seg }) => ({ segmentId: seg.id, label: seg.label, path: seg.navPath }));
}

const PRIORITY_TASK_DESCRIPTIONS: Record<ProfileCompletionSegmentId, string> = {
  profile_photo: 'Add a clear profile photo—bookers notice this first.',
  gallery: 'Upload photos and videos so bookers can see your performance.',
  social: 'Link Instagram, YouTube to your profile',
  personal: 'Add gender and location so we can match you to the right gigs.',
  usp: 'Highlight what makes you stand out from other artists.',
  story: 'Share your journey so clients connect with you.',
  performance: 'Set languages and typical set length for your shows.',
  achievements: 'List awards and milestones that build trust.',
  event_pricing: 'Verify contact details and add your event pricing.',
};

/**
 * Home “Priority Tasks” card order — keep in lockstep with the key order of `PRIORITY_TASK_DESCRIPTIONS`.
 */
const PRIORITY_TASK_CARD_ORDER: ProfileCompletionSegmentId[] = [
  'profile_photo',
  'gallery',
  'social',
  'personal',
  'usp',
  'story',
  'performance',
  'achievements',
  'event_pricing',
];

export type IncompletePriorityTask = {
  segmentId: ProfileCompletionSegmentId;
  label: string;
  description: string;
  path: string;
};

/** Next incomplete segments in `PRIORITY_TASK_CARD_ORDER`, capped for home “Priority Tasks” (e.g. 3 at a time). */
export function getIncompletePriorityTasks(
  state: ProfileCompletionDerived,
  limit = 3
): IncompletePriorityTask[] {
  if (!state.isReady || state.isIndeterminate) return [];
  const cap = Math.max(0, limit);
  const segById = new Map(SEGMENTS.map((seg, i) => [seg.id, { seg, segmentIndex: i }]));
  const out: IncompletePriorityTask[] = [];
  for (const id of PRIORITY_TASK_CARD_ORDER) {
    const row = segById.get(id);
    if (!row || state.segmentComplete[row.segmentIndex]) continue;
    out.push({
      segmentId: row.seg.id,
      label: row.seg.label,
      description: PRIORITY_TASK_DESCRIPTIONS[id],
      path: row.seg.navPath,
    });
    if (out.length >= cap) break;
  }
  return out;
}
